<template>
  <div class="rdi-modal-wrapper">
    <v-dialog
      v-model="$store.rdiStore.state.currentRDI"
      :max-width="$vuetify.breakpoint.mobile ? '100%' : 1400"
      :fullscreen="$vuetify.breakpoint.mobile"
      scrollable
      persistent
      :retain-focus="false"
      content-class="rdi-dialog"
    >
      <!-- Loading state -->
      <v-card v-if="$store.rdiStore.state.currentRDI.loading" class="modal-card loading-card">
        <div class="loading-header">
          <div class="modal-brand">
            <span class="brand-icon-dark"><v-icon small color="#2D4B95">mdi-file-document-multiple-outline</v-icon></span>
            <span class="brand-label-dark">Cargando RDI</span>
          </div>
          <button class="close-btn-dark" @click="closeModal">
            <v-icon size="18" color="#555">mdi-close</v-icon>
          </button>
        </div>
        <div class="loading-body">
          <v-progress-circular :size="40" :width="3" color="#2D4B95" indeterminate />
          <p class="loading-text">Cargando información…</p>
        </div>
      </v-card>

      <!-- Main card -->
      <v-card v-else class="modal-card">

        <ModalStickyHeader>
        <!-- TOP BAR -->
        <template #topbar>
        <div class="topbar">
          <div class="topbar-left">
            <span class="topbar-icon">
              <v-icon small color="#2D4B95">mdi-file-document-multiple-outline</v-icon>
            </span>
            <div class="topbar-titles">
							<p class="mb-0 caption">RDI <span class="caption font-weight-bold topbar-number">Nº{{ $store.rdiStore.state.currentRDI.number }}</span></p>
              <h2 class="topbar-title">
                {{ $store.rdiStore.state.currentRDI.title }}
              </h2>
              <p class="topbar-rdi" v-if="$store.rdiStore.state.currentRDI.task_relation">
                Tarea asociada #{{ $store.rdiStore.state.currentRDI.task_relation.task.number }} —
                {{ $store.rdiStore.state.currentRDI.task_relation.task.title }}
                <button class="inline-link-btn"
                  @click="viewRdi($store.rdiStore.state.currentRDI.task_relation.task.id, $options.filters.seenByMe($store.rdiStore.state.currentRDI.task_relation.task.seen_by_me))">
                  <v-icon size="13" color="#5E85EF">mdi-arrow-right-bold</v-icon>
                </button>
              </p>
            </div>
          </div>
          <div class="topbar-right">
            <HeaderButtons :closeModal="closeModal" :draftId="draftId"/>
          </div>
        </div>
        </template>

        <!-- STATUS STRIP -->
        <template #status-strip>
        <div class="status-strip">
          <div class="status-strip-pills">
            <span class="pill pill--blue">
              <v-icon size="12" color="#2D4B95">mdi-calendar-month-outline</v-icon>
              {{ $store.rdiStore.state.currentRDI.created_at | toDateTime }}
            </span>
            <span class="pill pill--gray">
              <v-icon size="12" color="#4B5563">mdi-clock-time-eight-outline</v-icon>
              Espera: {{ waitingTime }}
            </span>
            <span class="strip-separator">|</span>
            <span class="strip-label">Indicadores:</span>
            <v-tooltip bottom v-if="$store.rdiStore.state.currentRDI.status == 3">
              <template v-slot:activator="{ on, attrs }">
                <span class="pill pill--green" v-bind="attrs" v-on="on">
                  <v-icon size="10" color="#15803D">mdi-star</v-icon>
                  Finalizada
                </span>
              </template>
              <span>RDI finalizada el {{ $store.rdiStore.state.currentRDI.ended_at | toDateTime }}</span>
            </v-tooltip>
            <v-tooltip bottom v-if="checkIfNearDeadline($store.rdiStore.state.currentRDI)">
              <template v-slot:activator="{ on, attrs }">
                <span class="pill pill--orange" v-bind="attrs" v-on="on">
                  <v-icon size="10" color="#B45309">mdi-flag-outline</v-icon>
                  Fecha cercana
                </span>
              </template>
              <span>Se acerca la fecha crítica {{ $store.rdiStore.state.currentRDI.critical_date | toDate }}</span>
            </v-tooltip>
            <v-tooltip bottom v-if="$store.rdiStore.state.currentRDI.waiting_status != 1">
              <template v-slot:activator="{ on, attrs }">
                <span class="pill" :class="'pill--priority-' + getRdiPriorityColor($store.rdiStore.state.currentRDI)" v-bind="attrs" v-on="on">
                  <v-icon size="10">mdi-exclamation</v-icon>
                  {{ getRdiPriorityText($store.rdiStore.state.currentRDI) }}
                </span>
              </template>
              <span>Prioridad {{ getRdiPriorityText($store.rdiStore.state.currentRDI) }}</span>
            </v-tooltip>
            <span class="strip-no-indicators" v-if="$store.rdiStore.state.currentRDI.status != 3 && !checkIfNearDeadline($store.rdiStore.state.currentRDI) && $store.rdiStore.state.currentRDI.waiting_status == 1">Sin indicadores</span>
            <AutoCloseInfo v-if="selectedProject && currentRDI" :rdi="currentRDI" :project-config="currentRDI.project?.project_config" />
						<div class="d-flex align-center" style="gap:5px">
							<v-divider vertical />
							<p class="mb-0 text-primary" style="font-size: 10px;">Visto por:</p>
							<SeenByUsers
								v-model="seenByMenu"
								:seenByUsers="currentRDI.seen_by || []"
							/>
						</div>
          </div>
          <div class="status-strip-actions">
						<BotSummarizeMini v-if="!isLoading" />
            <div class="task-closed-notice-top" v-if="$store.rdiStore.state.currentRDI.status == 3">
              <v-icon size="18" color="#15803D">mdi-lock-check-outline</v-icon>
              <span>Esta RDI está cerrada.</span>
            </div>
            <RdiClosure v-else :rdi="currentRDI" @startCloseRDI="startCloseRDI" @reloadInfo="reloadInfo"/>
          </div>
        </div>
        </template>
        </ModalStickyHeader>

        <!-- TWO-COLUMN BODY -->
        <div class="modal-body" :class="{ resizing: isResizing }">

          <!-- LEFT: detail panel -->
          <div class="panel-detail" :style="$vuetify.breakpoint.mobile ? {} : { width: leftPanelWidth + '%' }">
            <div class="panel-scroll">

              <!-- Meta section -->
              <section class="meta-section">
                <div class="meta-two-columns">
                  <div class="meta-left-column">
                    <div class="meta-item">
                      <span class="meta-label">Proyecto</span>
                      <span class="meta-value">{{ $store.rdiStore.state.currentRDI.project.name }}</span>
                    </div>
                  </div>
                  <div class="meta-right-column">
                    <div class="meta-item">
                      <span class="meta-label">Materia</span>
                      <span class="meta-value">{{ $store.rdiStore.state.currentRDI.subject.name }}</span>
                    </div>
                  </div>
                </div>

                <v-divider class="meta-divider" />

                <div class="meta-two-columns">
                  <div class="meta-left-column">
                    <div class="meta-item">
                      <span class="meta-label">Especialidades</span>
                      <div>
                        <v-chip
                          v-for="(specialty, index) in $store.rdiStore.state.currentRDI.specialties"
                          :key="index"
                          color="#5E85EF"
                          dark
                          x-small
                          class="mr-1 mb-1">
                          {{ specialty.specialty.name }}
                        </v-chip>
                        <span class="meta-value" v-if="$store.rdiStore.state.currentRDI.specialties.length === 0">Sin especialidades</span>
                      </div>
                    </div>
                  </div>
                  <div class="meta-right-column">
										<RdiSubscribers
											:rdi="$store.rdiStore.state.currentRDI"
											:loading="loading"
											@openAddParticipant="addParticipantModal = true"
											@removeParticipant="removeParticipant"
										/>
                  </div>
                </div>
                <ItemsAssociationInput :item="$store.rdiStore.state.currentRDI" item_type="rdi" :beforeNavigate="minimizeRdi"/>
              </section>

              <!-- Description -->
              <section class="content-section">
                <h3 class="section-title">Descripción</h3>
                <div class="description-body" v-html="$store.rdiStore.state.currentRDI.description"></div>
								<MediaGallery :items="$store.rdiStore.state.currentRDI.attachments" />
                <GlobalFileViewer
                  :document-item="viewerDocumentItem"
                  :forge-item="viewerForgeItem"
                  @close="closeAttachmentViewer"
                  @minimize-file="minimizeAttachmentViewer"
                  @open-morphable="openItemMorphable"
                  @error="onViewerError"
                  @load="onViewerLoad"
                />
              </section>

              <!-- Attachments -->
              <section class="content-section">
                <div class="section-header-row">
                  <h3 class="section-title">Adjuntos</h3>
                  <button class="text-action-btn"
                    v-if="$store.rdiStore.state.currentRDI.attachments.filter(item => !item.pathmedia).length > 1"
                    :disabled="loadingFilePath"
                    @click="downloadAttachments($store.rdiStore.state.currentRDI.attachments)">
                    <v-icon size="14" color="#374151">mdi-download-multiple</v-icon>
                    Descargar todo
                  </button>
                </div>
                <div class="attachments-list" v-if="$store.rdiStore.state.currentRDI.attachments.filter(item => !item.pathmedia).length > 0">
                  <div class="attachment-row" v-for="(att, i) in $store.rdiStore.state.currentRDI.attachments.filter(item => !item.pathmedia)" :key="i">
                    <div class="attachment-info">
                      <v-icon size="16" color="#5E85EF">mdi-file-outline</v-icon>
                      <span class="attachment-name">{{ att.name }}</span>
                    </div>
                    <div class="attachment-actions">
                  <AttachmentPreviewButton
                    :attachment="att"
                    :loading="loadingFilePath"
                    compact
                    @preview="previewAttachment"
                  />
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <button class="icon-action-btn" v-bind="attrs" v-on="on" :disabled="loadingFilePath" @click="getFilesUrl(att, true)">
                            <v-icon size="14" color="#5E85EF">mdi-open-in-new</v-icon>
                          </button>
                        </template>
                        <span>Ver en navegador</span>
                      </v-tooltip>
                      <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                          <button class="icon-action-btn" v-bind="attrs" v-on="on" :disabled="loadingFilePath" @click="getFilesUrl(att, true, true)">
                            <v-icon size="14" color="#5E85EF">mdi-cloud-download-outline</v-icon>
                          </button>
                        </template>
                        <span>Descargar</span>
                      </v-tooltip>
                    </div>
                  </div>
                </div>
                <p class="empty-note" v-else>Sin adjuntos</p>
              </section>

            </div><!-- /panel-scroll -->
          </div><!-- /panel-detail -->

          <!-- Resizer (desktop only) -->
          <div class="panel-resizer" @mousedown="startResize" v-if="!$vuetify.breakpoint.mobile">
            <div class="resize-handle"></div>
          </div>

          <!-- RIGHT: interactions panel -->
          <div class="panel-interactions" :class="{ 'panel-interactions--editor-open': showEditor }">

            <!-- Header -->
            <div class="interactions-header">
              <div class="d-flex align-center" style="gap: 15px;">
                <h3 class="section-title" style="margin:0">Interacciones</h3>
                <div class="add-comment-section" v-if="$store.rdiStore.state.currentRDI.status != 3 && checkInteractPermission(['interact_rdi'])">
                  <button class="add-comment-btn" @click="toggleEditorWithScroll">
                    <v-icon size="16" color="#2D4B95">{{ showEditor ? 'mdi-close' : 'mdi-comment-plus-outline' }}</v-icon>
                    {{ showEditor ? 'Cancelar' : 'Agregar comentario' }}
                  </button>
                </div>
              </div>
              <div class="interactions-header-actions">
                <div class="search-wrap">
                  <v-icon size="15" color="#9CA3AF">mdi-magnify</v-icon>
                  <input class="search-field" placeholder="Buscar..." v-model="interactionSearch" type="text" />
                </div>
                <v-menu offset-y>
                  <template v-slot:activator="{ on, attrs }">
                    <button class="create-btn" v-bind="attrs" v-on="on">
                      <v-icon size="14" color="white">mdi-plus</v-icon>
                      Crear
                    </button>
                  </template>
                  <div class="create-menu">
                    <button class="create-menu-item" v-if="checkPermissions(['create_tasks'])" @click="sendToTask()">
                      <v-icon size="14" color="#2D4B95">mdi-clipboard-text-multiple-outline</v-icon>
                      Enviar a tarea
                    </button>
                    <button class="create-menu-item" v-if="checkPermissions(['create_tasks'])" @click="sendToAdditionalLikely()">
                      <v-icon size="14" color="#2D4B95">mdi-clipboard-text-multiple-outline</v-icon>
                      Posible adicional
                    </button>
                    <button class="create-menu-item" v-if="checkPermissions(['create_rdi'])" @click="sendToRDI()">
                      <v-icon size="14" color="#2D4B95">mdi-clipboard-text-multiple-outline</v-icon>
                      Enviar a RDI
                    </button>
                    <button class="create-menu-item" v-if="checkPermissions(['create_additionals'])" @click="sendToAdditional()">
                      <v-icon size="14" color="#2D4B95">mdi-clipboard-text-multiple-outline</v-icon>
                      Enviar a adicional
                    </button>
                  </div>
                </v-menu>
              </div>
            </div>

            <!-- Feed -->
            <div class="interactions-feed" v-if="$store.rdiStore.state.currentRDI.interactions.length > 0 || filteredOfficialInteractions.length > 0">
              <Interactions
								v-if="filteredOfficialInteractions.length > 0"
                itemType="rdi_interactions"
                :interactions="filteredOfficialInteractions"
                :searchText="interactionSearch"
                :item="currentRDI"
                @reload="reloadInfo"
                @reply-interaction="onReplyInteraction"
                :beforeNavigate="minimizeRdi"/>
							<v-divider class="my-4" v-if="filteredOfficialInteractions.length > 0"/>
              <Interactions
                itemType="rdi_interactions"
                :interactions="filteredInteractions"
                :searchText="interactionSearch"
                :item="currentRDI"
                @reload="reloadInfo"
                @reply-interaction="onReplyInteraction"
								:beforeNavigate="minimizeRdi"/>
            </div>
            <div class="empty-interactions" v-else>
              <v-icon size="36" color="#D1D5DB">mdi-chat-outline</v-icon>
              <p class="empty-note">Sin interacciones aún</p>
            </div>

            <!-- Compose area -->
            <div class="compose-area" v-if="$store.rdiStore.state.currentRDI.status != 3 && checkInteractPermission(['interact_rdi'])" ref="composeArea">
              <div v-if="showEditor">

              <!-- Response type toggle -->
              <div class="compose-type-row">
                <v-btn-toggle v-model="response_type" mandatory dense>
                  <v-btn x-small @click="is_response = false">Interacción</v-btn>
                  <v-btn x-small @click="is_response = true">Respuesta Oficial</v-btn>
                </v-btn-toggle>
              </div>

              <!-- Reply banner -->
              <div class="reply-banner" v-if="interactionParent">
                <div class="d-flex align-center" style="gap:8px;">
                  <v-icon size="14" color="#2D4B95">mdi-reply</v-icon>
                  <div>
                    <p class="mb-0 caption font-weight-bold" style="color:#2D4B95">Respondiendo a {{ interactionParent.user ? interactionParent.user.name : '' }}</p>
                    <div v-html="interactionParent.message" class="reply-preview"></div>
                  </div>
                </div>
                <button class="icon-action-btn" @click="clearReply">
                  <v-icon size="13">mdi-close</v-icon>
                </button>
              </div>

              <!-- Reply mode dialog -->
              <v-dialog v-model="replyModeDialog" max-width="420" persistent>
                <v-card>
                  <v-card-title class="subtitle-1 font-weight-bold text-primary pb-1">
                    ¿Cómo mostrar tu respuesta?
                  </v-card-title>
                  <v-card-text>
                    <p class="body-2 mb-3">
                      Puedes elegir cómo aparecerá tu respuesta en la conversación:
                    </p>
                    <v-radio-group v-model="replyShowInFeed" hide-details>
                      <v-radio :value="true" color="#2D4B95">
                        <template v-slot:label>
                          <div>
                            <p class="mb-0 body-2 font-weight-medium">Como mensaje nuevo al final</p>
                            <p class="mb-0 caption text--disabled">
                              Aparece al final de la conversación indicando a qué mensaje responde
                            </p>
                          </div>
                        </template>
                      </v-radio>
                      <v-divider class="my-2"></v-divider>
                      <v-radio :value="false" color="#2D4B95">
                        <template v-slot:label>
                          <div>
                            <p class="mb-0 body-2 font-weight-medium">Solo como subrespuesta directa</p>
                            <p class="mb-0 caption text--disabled">
                              Aparece anidada dentro del mensaje original, sin ocupar espacio al final
                            </p>
                          </div>
                        </template>
                      </v-radio>
                    </v-radio-group>
                  </v-card-text>
                  <v-card-actions class="pt-0 pb-3 px-4">
                    <v-spacer/>
                    <button class="text-action-btn" @click="cancelReplyMode">Cancelar</button>
                    <button class="create-btn ml-2" @click="confirmReplyMode">Confirmar</button>
                  </v-card-actions>
                </v-card>
              </v-dialog>

              <InteractionEditor
                v-model="message"
								item_type="rdi"
								:item="currentRDI"
                @paste-image="onPasteImage"
                @mentions-updated="onMentionsUpdated"
              />

              <div class="response-alert" v-if="is_response">
                <v-icon size="15" color="#B45309">mdi-alert-outline</v-icon>
                <span>Estás respondiendo de forma oficial. Esta interacción quedará marcada como importante.</span>
              </div>

              <!-- Files preview -->
              <div v-if="filesPreview.length > 0" class="attachments-preview">
                <button class="attach-toggle" @click="showAttachments = !showAttachments">
                  <span class="attach-dot"></span>
                  {{ filesPreview.length }} {{ filesPreview.length === 1 ? 'adjunto' : 'adjuntos' }}
                  <v-icon size="14" :class="['chevron', { open: showAttachments }]">mdi-chevron-down</v-icon>
                </button>
                <div v-if="showAttachments" class="attach-grid">
                  <div class="img-chip" v-for="(f, i) in filesPreview.filter(f => f.src)" :key="'img-'+i">
                    <img :src="f.src" class="img-thumb" />
                    <span v-if="f.source === 'library'" class="attachment-reference-tag">Biblioteca</span>
                    <button class="chip-remove" @click="removeAttachment(f, i)"><v-icon x-small color="white">mdi-close</v-icon></button>
                  </div>
                  <div class="file-chip" v-for="(f, i) in filesPreview.filter(f => !f.src)" :key="'file-'+i">
                    <v-icon small color="#5E85EF">mdi-file-outline</v-icon>
                    <span class="file-name">{{ f.name }}</span>
                    <span v-if="f.source === 'library'" class="attachment-reference-tag">Biblioteca</span>
                    <button class="chip-remove file" @click="removeAttachment(f, i)"><v-icon x-small>mdi-close</v-icon></button>
                  </div>
                </div>
              </div>

              <div class="compose-actions mt-1">
                <button class="send-btn" :disabled="loading" @click="preAddInteraction()">
                  <v-progress-circular v-if="loading" :size="14" :width="2" indeterminate color="white" />
                  <v-icon v-else size="15" color="white">mdi-send</v-icon>
                  {{ confirmButtonText }}
                </button>
                <div class="compose-secondary">
                  <AttachmentSourceButton
                    @files-selected="onFilesChanged"
                    @library-file-selected="onLibraryFileSelected"
                  />
                  <button class="text-action-btn" @click="showEditor = false; clearEditorContent()">
                    <v-icon size="15" color="#666">mdi-close</v-icon>
                    Cancelar
                  </button>
                </div>
              </div>
              </div>
            </div>

            <!-- <div class="task-closed-notice" v-else-if="$store.rdiStore.state.currentRDI.status == 3">
              <v-icon size="18" color="#15803D">mdi-lock-check-outline</v-icon>
              <span>Esta RDI está cerrada.</span>
            </div> -->

          </div><!-- /panel-interactions -->
        </div><!-- /modal-body -->

      </v-card>
    </v-dialog>

    <div v-if="isResizing" class="resize-overlay"></div>

		<AddParticipantModal
			:item="currentRDI"
			:addingToType="2" 
			:showDateSelectors="false"
			:addParticipantModal="addParticipantModal"
			:closeModal="closeAddParticipantModal"
			@reloadInfo="reloadInfo"
		/>
    <AcceptCancelModal :acceptRejectModal="acceptRejectModal" :acceptRejectModalData="acceptRejectModalData"
      :closeModal="closeModalAcceptCancel"/>
    <SendContentByEmailModal :sendContentByEmailModal="sendByEmailModal" :closeModal="() => sendByEmailModal = false" :sendingType="2"/>
  </div>
</template>
<script>
import moment from 'moment'
import _debounce from 'lodash/debounce'
import { v4 as uuidv4 } from 'uuid'
import EventBus from '@/helpers/event_bus'
import {getPublicUrl, getTareaByID} from '@/helpers/api/tasks'
import {getParentElement} from '@/helpers/api/general'
import {addInteraction, getRdiByID, closeRDI, updateRdi, deleteRdi, downloadPDF, removeRdiSubscriber} from '@/helpers/api/rdi'
import {getZippedFiles} from '@/helpers/api/fileUploads'
import {deleteFile} from '@/helpers/api/fileManager'
import {create as createDraftAPI, update as updateDraftAPI} from '@/helpers/api/drafts'
import MediaGallery from '@/components/Globals/Helpers/MediaGallery'
import GlobalFileViewer from '@/components/Globals/Helpers/GlobalFileViewer'
import AttachmentPreviewButton from '@/components/Globals/Helpers/AttachmentPreviewButton'
import Interactions from '@/components/Globals/Helpers/Interactions'
import InteractionEditor from '@/components/Globals/Helpers/InteractionEditor'
import SendContentByEmailModal from '@/components/Globals/Modals/General/SendContentByEmailModal'
import AcceptCancelModal from '@/components/Globals/Modals/General/AcceptCancelModal.vue'
import HeaderButtons from '@/components/Globals/Modals/Rdis/Parts/HeaderButtons'
import ItemsAssociationInput from '@/components/Globals/Helpers/ItemsAssociationInput'
import RdiClosure from '@/components/Views/Rdi/Parts/RdiClosure'
import AutoCloseInfo from '@/components/Views/Rdi/Parts/AutoCloseInfo'
import RdiSubscribers from '@/components/Views/Rdi/Parts/RdiSubscribers'
import AddParticipantModal from '@/components/Globals/Modals/General/AddParticipantModal'
import BotSummarizeMini from '@/components/Globals/Helpers/BotSummarizeMini'
import SeenByUsers from '@/components/Globals/Helpers/SeenByUsers'
import AttachmentSourceButton from '@/components/Globals/Helpers/AttachmentSourceButton'
import ModalStickyHeader from '@/components/Globals/Helpers/ModalStickyHeader'

export default {
  name: 'RDIModal',
  components: { ModalStickyHeader, MediaGallery, GlobalFileViewer, AttachmentPreviewButton, AttachmentSourceButton, SendContentByEmailModal, AcceptCancelModal, Interactions, InteractionEditor, HeaderButtons, ItemsAssociationInput, RdiClosure, AutoCloseInfo, RdiSubscribers, AddParticipantModal, BotSummarizeMini, SeenByUsers },
  computed: {
		selectedProject(){
			return this.$store.projectsStore.state.selectedProject
		},
    currentRDI() {
      return this.$store.rdiStore.state.currentRDI
    },
		isLoading() {
			return this.$store.rdiStore.state.currentRDI.loading
		},
		waitingTime(){

			const start = moment(this.currentRDI.created_at);
			const end = this.currentRDI.ended_at ? moment(this.currentRDI.ended_at) : moment();

			let years = end.diff(start, 'years');
			start.add(years, 'years');

			let months = end.diff(start, 'months');
			start.add(months, 'months');

			let days = end.diff(start, 'days');
			start.add(days, 'days');

			let hours = end.diff(start, 'hours');

			const parts = [];

			if (years) parts.push(`${years} ${years === 1 ? 'año' : 'años'}`);
			if (months) parts.push(`${months} ${months === 1 ? 'mes' : 'meses'}`);
			if (days) parts.push(`${days} ${days === 1 ? 'día' : 'días'}`);
			if (hours) parts.push(`${hours} ${hours === 1 ? 'hora' : 'horas'}`);

			if (parts.length === 0) return '0 horas';
			if (parts.length === 1) return parts[0];

			return parts.slice(0, -1).join(', ') + ' y ' + parts[parts.length - 1];
		},
		gotOficialResponse() {
			return this.$store.rdiStore.state.currentRDI.interactions.some((item) => { return item.is_response })
		},
		checkApprovalChecks(){
			const creator = this.$store.rdiStore.state.currentRDI.approval_checks.some((item) => {
				return item.user_id == this.$store.rdiStore.state.currentRDI.created_by
			})
			const jefe_ot = this.$store.rdiStore.state.currentRDI.approval_checks.some((item) => {
				return item.job_type == 2
			})
			const ito = this.$store.rdiStore.state.currentRDI.approval_checks.some((item) => {
				return item.job_type == 4
			})
			return creator && jefe_ot && ito
		},
		draftToLoad(){
			return this.$store.rdiStore.state.draftToLoad
		},
		officialInteractions(){
			if(this.currentRDI.status != 3) return []
			return this.$store.rdiStore.state.currentRDI.interactions.filter((item) => { return item.is_response })
		},
		orderedInteractions(){
			return [...this.$store.rdiStore.state.currentRDI.interactions]
			// return [...this.$store.rdiStore.state.currentRDI.interactions]
			// 	.sort((a, b) => Number(b.is_response) - Number(a.is_response));
		},
		filteredInteractions() {
			const items = this.orderedInteractions
			if (this.interactionSearch && this.interactionSearch !== '' && items) {
				return items.filter(item =>
					item.message.toLowerCase().includes(this.interactionSearch.toLowerCase())
				)
			}
			return items ? [...items] : []
		},
		filteredOfficialInteractions() {
			const items = this.officialInteractions
			if (this.interactionSearch && this.interactionSearch !== '' && items) {
				return items.filter(item =>
					item.message.toLowerCase().includes(this.interactionSearch.toLowerCase())
				)
			}
			return items ? [...items] : []
		},
  },
  watch: {
    currentRDI(newData) {
      if(newData && !newData.loading && newData.id){
        setTimeout(() => {
          this.pasteListener()
        }, 500);
      }
    },
    message(val) {
      if(val && val.length > 0) {
        this.handleFormChangeDebounced()
      }
    },
    files() {
      this.handleFormChangeDebounced()
    }
  },
  data: () => ({
    dialog: true,
    leftPanelWidth: 50,
    isResizing: false,
    showMore: false,
		interactionSearch: '',
		showAttachments: true,
		showEditor: false,
    message: '',
    response_type: 0,
    need_reload: false,
    files: [],
    filesPreview: [],
    libraryFiles: [],
    loadingFilePath: false,
    viewerDocumentItem: null,
    viewerForgeItem: null,
    is_response: false,
    loading: false,
		loadingRemoveParticipant: false,
    addParticipantModal: false,
    changeInChecklists: false,
    loadingChangeUser: false,
		loadingPDFDownload: false,
		sendByEmailModal: false,
		cancelTimer: 4,
		inCancelTime: false,
		cancelInterval: null,
		confirmButtonText: 'Responder',
		draftId: null,
		draftCreated: false,
		draftUpdateInterval: 20,
		draftSaveTimer: null,
    draftCreationPromise: null,
		handleFormChangeDebounced: null,
		isResetting: false,
		loadingEdit: false,
    acceptRejectModal: false,
    acceptRejectModalData: null,
		interactionParent: null,
		replyModeDialog: false,
		replyShowInFeed: true,
		newInteractionMentions: [],
		seenByMenu: false
  }),
  mounted(){
    this.setListeners()
    this.handleFormChangeDebounced = _debounce(() => this.checkAndCreateDraft(), 1000)
    window.addEventListener('mousemove', this.handleResize)
    window.addEventListener('mouseup', this.stopResize)
		if(this.draftToLoad){
			this.loadDraft(this.draftToLoad)
		}
  },
	beforeDestroy(){
		window.removeEventListener('mousemove', this.handleResize)
		window.removeEventListener('mouseup', this.stopResize)
		EventBus.$off('completedFileUpload')
		EventBus.$off('reload_element')
		if(this.draftSaveTimer){
			clearInterval(this.draftSaveTimer)
			this.draftSaveTimer = null
		}
	},
  methods: {
		startResize(e) {
			this.isResizing = true
			e.preventDefault()
		},
		handleResize(e) {
			if (!this.isResizing) return
			const dialog = document.querySelector('.rdi-dialog')
			if (!dialog) return
			const modalBody = dialog.querySelector('.modal-body')
			if (!modalBody) return
			const rect = modalBody.getBoundingClientRect()
			const newLeftWidth = ((e.clientX - rect.left) / rect.width) * 100
			if (newLeftWidth >= 25 && newLeftWidth <= 75) this.leftPanelWidth = newLeftWidth
		},
		stopResize() {
			if (this.isResizing) this.isResizing = false
		},
    closeAddParticipantModal() { this.addParticipantModal = false },
    async removeParticipant(participant) {
      this.loadingRemoveParticipant = true
      const rdi = await removeRdiSubscriber({ subscriber_id: participant.user_id, rdi_id: this.currentRDI.id })
      this.loadingRemoveParticipant = false
      if (rdi.code === 200) this.reloadInfo()
    },
		onMentionsUpdated (userIds) {
			this.newInteractionMentions = userIds
		},
		toggleEditorWithScroll() {
			this.showEditor = !this.showEditor
			if (this.showEditor) {
				setTimeout(() => {
					if (this.$refs.composeArea) {
						this.$refs.composeArea.scrollIntoView({ behavior: 'smooth', block: 'end' })
					}
				}, 300)
			}
		},

		clearEditorContent() {
			this.message = ''
			this.files = []
			this.filesPreview = []
			this.interactionParent = null
			this.replyShowInFeed = true
		},
		onReplyInteraction (interaction) {
			this.interactionParent = interaction
			this.replyModeDialog = true
			if (!this.showEditor) this.toggleEditorWithScroll()
		},
		confirmReplyMode () {
			this.replyModeDialog = false
		},
		cancelReplyMode () {
			this.replyModeDialog = false
			this.interactionParent = null
			this.replyShowInFeed = true
		},
		clearReply () {
			this.interactionParent = null
			this.replyShowInFeed = true
		},
		sendToTask(){
			const t = this.$store.rdiStore.state.currentRDI
			const associations = [{ type: 'rdi', id: t.id, title: t.title, number: t.number }]
      const data = {
        title: t.title,
        description: t.description,
        rdi_id: t.id,
        associations
      }
      this.$store.consultasStore.commit('setBorradorTarea', data)
      this.$store.consultasStore.commit('setCreateTareaModal', true)
    },
		sendToAdditionalLikely(){
			const t = this.$store.rdiStore.state.currentRDI
			const associations = [{ type: 'rdi', id: t.id, title: t.title, number: t.number }]
      const data = {
        title: t.title,
        description: t.description,
        rdi_id: t.id,
        associations
      }
      this.$store.consultasStore.commit('setBorradorTarea', data)
      this.$store.adicionalesStore.commit('setCreatePosibleAdicionalModal', true)
    },
    sendToRDI(){
			const t = this.$store.rdiStore.state.currentRDI
			const associations = [{ type: 'rdi', id: t.id, title: t.title, number: t.number }]
      const data = {
        title: t.title,
        description: t.description,
        rdi_id: t.id,
        associations
      }
      this.$store.rdiStore.commit('setInitialDataRDI', data)
      this.$store.rdiStore.commit('setCreateRDIModal', true)
    },
    sendToAdditional(){
			const t = this.$store.rdiStore.state.currentRDI
			const associations = [{ type: 'rdi', id: t.id, title: t.title, number: t.number }]
			const data = {
				title: t.title,
				description: t.description,
        rdi_id: t.id,
        associations
			}
			this.$store.adicionalesStore.commit('setInitialDataAdicional', data)
			this.$store.adicionalesStore.commit('setCreateAdicionalModal', true)
			this.closeModal()
    },
    async minimizeRdi(){
      const data = {
        task_id: this.currentRDI.id,
        title: this.currentRDI.title,
        type: 'RDI',
        type_id: 2,
            number: this.currentRDI.number,
				draft_id: null
      }
      const draftId = await this.closeModal()
      data.draft_id = draftId
      this.$store.globalStore.commit('addTaskToTasksHandler', data)
    },
    checkInteractPermission(permissionToCheck){
      return this.checkPermissions(permissionToCheck)
    },
		changeInSubscription(value){
			this.editRdiForm.subscribe_status_mail = value
		},
    isUserPart(){
      return this.$store.userStore.state.userInfo.id === this.$store.rdiStore.state.currentRDI.created_by_user.id || this.$store.userStore.state.userInfo.id === this.$store.rdiStore.state.currentRDI.responsable_user.id
    },
		isMyRdi(){
      return this.$store.userStore.state.userInfo.id === this.$store.rdiStore.state.currentRDI.created_by_user.id
		},
		filteredSearch(items){
			if(this.interactionSearch && this.interactionSearch !== '' && items){
				const filtered = items.filter((item) => {
					return item.message.toLowerCase().includes(this.interactionSearch.toLowerCase())
				})
				return filtered
			}
			return items
		},
    pasteListener(){
      const textEditor = document.querySelector('#interaction-texteditor .ProseMirror')
      if(textEditor){
        textEditor.addEventListener('paste', this.pasteListenerHandler)
      }
    },
		async pasteListenerHandler(event) {
			const dT = event.clipboardData || window.clipboardData
			const file = dT.files[0]
			if(file){
				this.onFileChanged(null, file)
			}
		},
		async onPasteImage(file){
			await this.createDraft(true)
			this.onFileChanged(null, file)
		},
    changedChecklist(){
      this.changeInChecklists = true
    },
    setListeners(){
      EventBus.$on('completedFileUpload', async (payload) => {
        if(payload && payload.draft_id === this.draftId){
          const findIndex = this.filesPreview.findIndex((file) => file.uuid === payload.uuid)
          if(findIndex !== -1){
            this.$set(this.filesPreview[findIndex], 'id', payload.file.id)
          }
        }
				else if(payload && payload.rdi_id && this.$store.rdiStore.state.currentRDI && payload.rdi_id == this.$store.rdiStore.state.currentRDI.id){
					this.reloadInfo()
        }
      })
      EventBus.$on('reload_element', async (payload) => {
        if (payload?.type == 'rdi' && payload?.id == this.$store.rdiStore.state.currentRDI.id){
					this.reloadInfo()
        }
      })
    },
		async reloadInfo(){
			const rdi = await getRdiByID({rdi_id: this.$store.rdiStore.state.currentRDI.id})
			if(rdi.code === 200){
				let rdiData = rdi.rdi
				this.$store.rdiStore.commit('setCurrentRDI', rdiData)
			}
		},
    async getRDIList(){
      let project_id = null
      if(this.selectedProject){
        project_id = this.selectedProject.id
      }
      else{
        const id = localStorage.getItem('selectedProjectID')
        project_id = parseInt(id)
      }
      if(project_id){
        this.$store.rdiStore.dispatch('getRDI', {project_id})
      }
    },
    checkAndCreateDraft(){
      if(!this.draftCreated && !this.draftId){
        this.createDraft()
      }
    },
    async createDraft(force_create = false){
      if(this.draftId) return this.draftId
      if(this.draftCreated) return this.draftCreationPromise
      if ((!this.message || this.message.trim() === '') && !force_create) return null
      this.draftCreated = true
      
      const draftData = {
        type: 6,
				project_id: this.selectedProject.id,
        type_id: this.currentRDI.id,
        data: JSON.stringify({
          message: this.message,
					title: this.currentRDI.title,
            number: this.currentRDI.number,
          library_file_ids: this.libraryFiles.map(file => file.id),
          library_files: this.libraryFiles.map(file => this.serializeLibraryFile(file))
        })
      }
      
      try {
        this.draftCreationPromise = createDraftAPI(draftData)
        const response = await this.draftCreationPromise
        if(response.code === 200){
					this.draftId = response.draft.id
          this.startDraftUpdateTimer()
					EventBus.$emit('reloadDraftList')
					return this.draftId
        }
      } catch (error) {
        console.error('Error creating draft:', error)
        this.draftCreated = false
        throw error
      } finally {
			this.draftCreationPromise = null
      }
    },
    startDraftUpdateTimer(){
      if(this.draftSaveTimer){
        clearInterval(this.draftSaveTimer)
        this.draftSaveTimer = null
      }
      
      this.draftSaveTimer = setInterval(() => {
        this.updateDraft()
      }, this.draftUpdateInterval * 1000)
    },
    async updateDraft(){
      if(!this.draftId) return
      
      const draftData = {
        id: this.draftId,
        type: 6,
				project_id: this.selectedProject.id,
        type_id: this.currentRDI.id,
        data: JSON.stringify({
          message: this.message,
					title: this.currentRDI.title,
          number: this.currentRDI.number,
          library_file_ids: this.libraryFiles.map(file => file.id),
          library_files: this.libraryFiles.map(file => this.serializeLibraryFile(file))
        })
      }
      
      try {
        await updateDraftAPI(draftData)
      } catch (error) {
        console.error('Error updating draft:', error)
      }
    },
    async loadDraft(draft){
			if(draft && draft.data){
				this.isResetting = true
				this.message = draft.data.message || ''
        this.libraryFiles = Array.isArray(draft.data.library_files) ? draft.data.library_files : []

				try{
					const filePreviews = []
					
					if(draft.files && Array.isArray(draft.files)){
						for(const file of draft.files){
							if(file.mime_type && file.mime_type.includes('image')){
								if(file.pathmedia?.path){
									filePreviews.push({src: file.pathmedia.path, id: file.id, name: file.name})
								}
								else{
									filePreviews.push({noImage: true, id: file.id, name: file.name})
								}
							}
							else{
								filePreviews.push({noImage: true, id: file.id, name: file.name})
							}
						}
					}
					console.log('filePreviews', filePreviews)
					this.filesPreview = filePreviews
          for (const file of this.libraryFiles) {
            this.filesPreview.push(file.pathmedia?.path
              ? { src: file.pathmedia.path, id: file.id, name: file.name, source: 'library' }
              : { noImage: true, id: file.id, name: file.name, source: 'library' })
          }
				}
				catch(error){
					console.error('Error al cargar archivos del draft:', error)
				}
				
				this.draftId = draft.id
				this.draftCreated = true
				this.startDraftUpdateTimer()

				this.$nextTick(() => {
					this.isResetting = false
				})

				const data = {
					message: 'Borrador cargado correctamente. Los cambios se guardarán automáticamente.',
					title: 'Borrador Cargado',
					created: new Date(),
					type: 'success',
					icon: 'mdi-check-circle',
					color: '#3EBFA3'
				}
				this.$store.globalStore.dispatch('addMessage', data)
				this.$store.rdiStore.commit('setDraftToLoad', null)
			}
    },
    getRdiPriorityColor(rdi){
      if(rdi.priority === 3){
        return 'high'
      }
      else if(rdi.priority === 2){
        return 'medium'
      }
      return 'normal'
    },
    getRdiPriorityText(rdi){
      if(rdi.priority === 3){
        return 'Inmediata'
      }
      else if(rdi.priority === 2){
        return 'Media'
      }
      return 'Estándar'
    },
    checkIfNearDeadline(rdi){
      if(!rdi.critical_date){ return false }
      const now = moment()
      const rdi_date = moment(rdi.critical_date)
      return rdi_date.diff(now, 'days') <= 2
    },
    getColorStatus(){
      if(this.$store.rdiStore.state.currentRDI.status === 1){
        return '#3EBFA3'
      }
      return '#F4516C'
    },
    async closeModal(){
      if (!this.draftId && this.draftCreationPromise) await this.draftCreationPromise
      if (!this.draftId && this.message && this.message.trim()) await this.createDraft()
      if (this.draftId) await this.updateDraft()
      const savedDraftId = this.draftId
      const reload = this.need_reload || !this.$store.rdiStore.state.currentRDI.prev_seen_by_me
      this.$store.rdiStore.commit('setCurrentRDI', null)
      if(reload){
        this.getRDIList()
      }
      this.need_reload = false
      this.loading = false
      this.files = []
      this.filesPreview = []
			return savedDraftId
    },
    async onFilesChanged(e) {
			await this.createDraft(true);
        const files = Array.isArray(e) ? e : (e?.target?.files || [])
      for (let i = 0; i < files.length; i++) {
        await this.onFileChanged(null, files[i])
      }
    },
		async onFileChanged(e, file = null) {
			const newFile = e?.target?.files[0] || file
			if(!newFile) return
      if (!this.draftId && !this.draftCreated) await this.createDraft(true)

      const uuid = uuidv4()
      if(newFile.type.includes('image')){
        const base64File = await this.toBase64(newFile)
        this.filesPreview.push({src: base64File, name: newFile.name, uuid})
      }
      else{
        this.filesPreview.push({noImage: true, name: newFile.name, uuid})
      }
      
      const fileToUpload = {
        type: 13,
        payload: {draft_id: this.draftId, project_id: this.selectedProject.id, uuid},
        description: this.currentRDI.title,
        total: newFile.size,
        uuid,
        file: newFile
      }
      this.$store.globalStore.commit('setFilesToUploadCenter', fileToUpload)
    },
      removeAttachment(file){
        const previewIndex = this.filesPreview.indexOf(file)
        if (previewIndex !== -1) this.filesPreview.splice(previewIndex, 1)
        if (file.source === 'library') {
          const libraryIndex = this.libraryFiles.findIndex(item => Number(item.id) === Number(file.id))
          if (libraryIndex !== -1) this.libraryFiles.splice(libraryIndex, 1)
          this.updateDraft()
          return
        }
			if(file.id){
				deleteFile({file_id: file.id})
			}
		},
      async onLibraryFileSelected(file) {
        if (!file || !file.id || this.libraryFiles.some(item => Number(item.id) === Number(file.id))) return
        this.libraryFiles.push(file)
        this.filesPreview.push(file.pathmedia?.path
          ? { src: file.pathmedia.path, id: file.id, name: file.name, source: 'library' }
          : { noImage: true, id: file.id, name: file.name, source: 'library' })
        await this.createDraft(true)
        await this.updateDraft()
      },
      serializeLibraryFile(file) {
        return { id: file.id, name: file.name, ext: file.ext, mime_type: file.mime_type, pathmedia: file.pathmedia || null }
      },
    toBase64(file) {
      return new Promise((resolve) => {
        var reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = function () {
          resolve(reader.result)
        };
        reader.onerror = function () {
          resolve(null)
        };
      })
    },
    async getFilesUrl(attachments, singleFile = false, download = false){
      if(singleFile){ attachments = [attachments] }
      this.loadingFilePath = true
      const files = await getPublicUrl({attachments, force_download: download})
      if(files.code === 200){
        files.files.forEach((file) => {
          window.open(file.url);
        })
      }
      this.loadingFilePath = false
    },
    previewAttachment(attachment){
      this.viewerDocumentItem = null
      this.viewerForgeItem = null

      if(attachment?.pathdoc?.path){
        this.viewerDocumentItem = attachment
        return
      }

      if(attachment?.file_type === 'forge' || attachment?.forge_file?.translated){
        this.viewerForgeItem = attachment
        return
      }

      this.$store.globalStore.dispatch('addMessage', {
        message: 'Este archivo no tiene un visor disponible.',
        title: 'Vista previa no disponible',
        created: new Date(),
        type: 'warning',
        icon: 'mdi-eye-off-outline',
        color: '#FB8500'
      })
    },
    closeAttachmentViewer(){
      this.viewerDocumentItem = null
      this.viewerForgeItem = null
    },
    minimizeAttachmentViewer(item){
      if(!item) return
      this.$store.globalStore.commit('addFileToFileManagerHandler', item)
      this.closeAttachmentViewer()
    },
    onViewerError(){
      console.error('Attachment viewer failed to load')
    },
    onViewerLoad(){},
    async openItemMorphable(item){
      const typeMap = {
        'App\\Models\\TasksRestrictions': 'task',
        'App\\Models\\TasksRestrictionsInteractions': 'task_interaction',
        'App\\Models\\Rdi': 'rdi',
        'App\\Models\\RdiInteractions': 'rdi_interaction',
        'App\\Models\\Additional': 'additional',
        'App\\Models\\AdditionalRevision': 'additional_revision',
        'App\\Models\\AdditionalObservation': 'additional_observation'
      }
      const parentTypesMap = {
        'App\\Models\\TasksRestrictions': 'task',
        'App\\Models\\TasksRestrictionsInteractions': 'task',
        'App\\Models\\Rdi': 'rdi',
        'App\\Models\\RdiInteractions': 'rdi',
        'App\\Models\\Additional': 'additional',
        'App\\Models\\AdditionalRevision': 'additional',
        'App\\Models\\AdditionalObservation': 'additional'
      }
      if(!item || !typeMap[item.morphable_type] || !item.morphable_id) return

      const parentType = parentTypesMap[item.morphable_type]
      const data = {
        item_id: item.morphable_id,
        item_type: typeMap[item.morphable_type]
      }

      if(parentType === 'task'){
        this.$store.consultasStore.commit('setCurrentTarea', { loading: true })
      }
      else if(parentType === 'rdi'){
        this.$store.rdiStore.commit('setCurrentRDI', { loading: true })
      }
      else if(parentType === 'additional'){
        this.$store.adicionalesStore.commit('setCurrentAdicional', { loading: true })
      }

      this.closeAttachmentViewer()
      const element = await getParentElement(data)
      if(element.code !== 200) return

      if(parentType === 'task'){
        this.$store.consultasStore.commit('setCurrentTarea', element.task)
      }
      else if(parentType === 'rdi'){
        this.$store.rdiStore.commit('setCurrentRDI', element.rdi)
      }
      else if(parentType === 'additional'){
        this.$store.adicionalesStore.commit('setCurrentAdicional', element.additional)
      }
    },
    async downloadAttachments(attachments){
      this.loadingFilePath = true
      const files = await getZippedFiles({files_to_zip: attachments})
      if(files.code == 200){
          // this.downloadFile(files.fileurl, 'archivo.zip')
          window.open(files.fileurl);
      }
      this.loadingFilePath = false
    },
    async preAddInteraction(){
			if(this.inCancelTime){
				clearInterval(this.cancelInterval)
				this.confirmButtonText = 'Confirmar'
				this.inCancelTime = false
				return null
			}
      if(this.message === '' || !this.message){
        const datas = {
         message: 'Por favor escribe un mensaje. Este campo no puede estar vacio.',
         title: 'Error',
          created: new Date(),
          type: 'success',
          icon: 'mdi-alert-circle',
          color: '#fb8c00'
        }
        this.$store.globalStore.dispatch('addMessage', datas)
        return false
      }

			this.inCancelTime = true
			this.cancelTimer = 4
			this.confirmButtonText = 'Cancelar (' + this.cancelTimer + ')'
			this.cancelInterval = setInterval(() => {
				this.cancelTimer--
				this.confirmButtonText = 'Cancelar (' + this.cancelTimer + ')'
				if(this.cancelTimer == 0){
					this.inCancelTime = false
					this.confirmButtonText = 'Responder'
					clearInterval(this.cancelInterval)
					this.beforeAddInteraction()
				}
			}, 1000);
    },
		beforeAddInteraction(closing = false){
      return new Promise((resolve) => {
				let promises = []
				if(!closing && (this.message === '' || !this.message)){
					const datas = {
					message: 'Por favor escribe un mensaje. Este campo no puede estar vacio.',
					title: 'Error',
						created: new Date(),
						type: 'success',
						icon: 'mdi-alert-circle',
						color: '#fb8c00'
					}
					this.$store.globalStore.dispatch('addMessage', datas)
					resolve(false)
					return false
				}
				else if(closing && (this.message === '' || !this.message)){
					promises.push(
						this.addInteraction(
							this.$store.rdiStore.state.currentRDI,
							'RDI cerrada por usuario',
							false,
							[],
							{ skipNotificationEmail: true }
						)
					)
				}

				if(!closing || (this.message !== '' && this.message)){
					promises.push(
						this.addInteraction(
							this.$store.rdiStore.state.currentRDI,
							this.message,
							this.is_response,
							this.files,
							{ skipNotificationEmail: closing }
						)
					)
				}

				Promise.all(promises)
          .then(function(){
            resolve(true)
          })
          .catch(function(){
            resolve(false)
          })
			})
		},
    async addInteraction(rdi, message, is_response, files, options = {}){
      return new Promise((resolve) => {
				this.loading = true
				const biggerFiles = []
				const formData = new FormData()
				formData.append('rdi_id', rdi.id)
				formData.append('message', message)
				formData.append('is_response', is_response)
				formData.append('skip_notification_email', options.skipNotificationEmail ? 'true' : 'false')
				if(this.draftId){
					formData.append('draft_id', this.draftId)
				}
				if(this.interactionParent){
					formData.append('parent_id', this.interactionParent.id)
					formData.append('show_in_feed', this.replyShowInFeed ? 1 : 0)
				}
				if(this.newInteractionMentions && this.newInteractionMentions.length > 0){
					this.newInteractionMentions.forEach(mention => {
						formData.append('mentions[]', mention)
					})
				}
        this.libraryFiles.forEach(file => formData.append('library_file_ids[]', file.id))
				files.forEach((file) => {
					if(file && file.size < 1000){
						formData.append('files[]', file)
					}
					else if(file){
						biggerFiles.push({
							type: 4,
							payload: {'rdi_id': rdi.id, 'project_id': this.selectedProject.id},
							description: rdi.title,
							total: file.size,
							uuid: uuidv4(),
							file
						})
					}
				})
				addInteraction(formData)
					.then((interaction) => {
						if(interaction.code === 200){
							if(biggerFiles.length > 0){
								biggerFiles.map(file => {
									file.payload = Object.assign(file.payload, {rdi_interaction_id: interaction.interaction, project_id: this.selectedProject.id})
									this.$store.globalStore.commit('setFilesToUploadCenter', file)
								})
							}
							this.need_reload = true
							this.message = ''
							this.files = []
							this.filesPreview = []
              this.libraryFiles = []
							this.interactionParent = null
							this.replyShowInFeed = true
							this.newInteractionMentions = []
							this.showEditor = false
							let rdiData = interaction.rdi
							this.$store.rdiStore.commit('setCurrentRDI', rdiData)
							resolve(true)
						} else {
							resolve(false)
						}
						this.loading = false
					})
					.catch(() => {
						resolve(false)
					})
			})
    },
		async modifyRdi(){
			this.loadingEdit = true
			this.editRdiForm.id = this.$store.rdiStore.state.currentRDI.id
			const rdiUpdate = await updateRdi(this.editRdiForm)
      if(rdiUpdate.code == 200){
        this.$store.rdiStore.commit('setCurrentRDI', rdiUpdate.rdi)
        this.getRDIList()
				const data = {
					message: 'Se ha modificado correctamente la RDI',
					title: 'RDI modificada',
					created: new Date(),
					type: 'success',
					icon: 'mdi-check-circle',
					color: '#3EBFA3',
				}
				this.$store.globalStore.dispatch('addMessage', data)
				this.$store.notificationsStore.dispatch('getReminders')
      }
			this.loadingEdit = false
		},
    closeModalAcceptCancel(status = false){
      this.acceptRejectModal = false
      if(status && this.acceptRejectModalData && this.acceptRejectModalData.type === 1){
        this.closeRDI()
      }
      if(status && this.acceptRejectModalData && this.acceptRejectModalData.type === 2){
        this.deleteRdi()
      }
      this.acceptRejectModalData = null
    },
		startCloseRDI(){
      this.acceptRejectModalData = {
        title: '¿Cerrar la RDI Nº ' + this.$store.rdiStore.state.currentRDI.number + '?',
        content_a: 'Por favor confirma si deseas cerrar la Rdi indicada.',
				type: 1 //1: Close, 2: delete
      }
			if(!this.gotOficialResponse && (this.message == '' || !this.is_response)){
				this.acceptRejectModalData.content_b = 'Ten en cuenta que estás cerrando la RDI sin una respuesta oficial que indique algún estado de cierre. Si deseas agregar una respuesta oficial cancela e ingrésala antes de finalizar.'
			}
      this.acceptRejectModal = true
		},
    async closeRDI(){
      const closingComment = (this.message && String(this.message).trim() !== '') ? this.message : null
      await this.beforeAddInteraction(true)
      const rdi = await closeRDI({
				rdi_id: this.$store.rdiStore.state.currentRDI.id,
				closing_comment: closingComment
			})
      if(rdi.code === 200){
        let currentRDI = {...this.$store.rdiStore.state.currentRDI}
        currentRDI.status = 3
        this.$store.rdiStore.commit('setCurrentRDI', currentRDI)
				this.$store.notificationsStore.dispatch('getReminders')
        this.$store.projectsStore.dispatch('getDashboardInfo')
        this.need_reload = true
        this.message = ''
        this.files = []
        this.filesPreview = []
      }
    },
    startDeleteRdi(){
      this.acceptRejectModalData = {
        title: '¿Eliminar la RDI Nº ' + this.$store.rdiStore.state.currentRDI.number + '?',
        content_a: 'Por favor confirma si deseas elliminar la RDI indicada.',
				type: 2 //1: Close, 2: delete
      }
      this.acceptRejectModal = true
    },
		async deleteRdi(){
			const task = await deleteRdi({task_id: this.$store.rdiStore.state.currentRDI.id})
      if(task.code === 200){
        this.getRDIList()
				this.$store.notificationsStore.dispatch('getReminders')
				this.$store.rdiStore.commit('setCurrentRDI', null)
				const data = {
					message: 'Se ha eliminado correctamente la RDI',
					title: 'RDI eliminada',
					created: new Date(),
					type: 'success',
					icon: 'mdi-check-circle',
					color: '#3EBFA3',
				}
				this.$store.globalStore.dispatch('addMessage', data)
				this.$store.notificationsStore.dispatch('getReminders')
			}
		},
    minimizeItem(){
      const data = {
        task_id: this.$store.rdiStore.state.currentRDI.id,
        title: this.$store.rdiStore.state.currentRDI.title,
        type: 'RDI',
        type_id: 2,
        number: this.$store.rdiStore.state.currentRDI.number
      }
      this.$store.globalStore.commit('addTaskToTasksHandler', data)
      this.closeModal()
    },
		async viewRdi(task_id, seen_by_me){
			this.$store.consultasStore.commit('setCurrentTarea', {loading: true})
			const tarea = await getTareaByID({task_id})
			if(tarea.code === 200){
				let tareaData = tarea.task
				tareaData.prev_seen_by_me = seen_by_me
				this.$store.consultasStore.commit('setCurrentTarea', tareaData)
			}
		},
		async downloadPDF(){
			this.loadingPDFDownload = true
			const pdf_url = await downloadPDF({rdi_id: this.$store.rdiStore.state.currentRDI.id})
      if(pdf_url.code == 200){
          window.open(pdf_url.pdf_url);
      }
			this.loadingPDFDownload = false
		}
  }
};
</script>

<style scoped>
.attachment-reference-tag {
  display: inline-flex;
  align-items: center;
  margin-left: 6px;
  padding: 1px 5px;
  border-radius: 4px;
  background: #eef2ff;
  color: #2d4b95;
  font-size: 10px;
  line-height: 14px;
  white-space: nowrap;
}

:root {
  --blue: #2D4B95;
  --blue-sec: #5E85EF;
  --blue-light: #EEF2FF;
  --text: #1A1D23;
  --text-muted: #6B7280;
  --border: #E5E8EF;
  --surface: #F8F9FC;
  --radius: 12px;
}

/* Wrapper & dialog */
.rdi-modal-wrapper::v-deep .rdi-dialog {
  border-radius: 16px !important;
  overflow: hidden;
}

/* Main card */
.modal-card {
  border-radius: 16px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
}

/* TOP BAR */
.topbar {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 18px 24px 16px;
  background: #F4F6FB;
  border-bottom: 1px solid #E0E5F0;
  flex-shrink: 0;
}
.topbar-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  min-width: 0;
}
.topbar-icon {
  width: 36px;
  height: 36px;
  border-radius: 10px;
  background: #E8EEFA;
  border: 1px solid #C8D4F0;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.topbar-titles { flex: 1; min-width: 0; }
.topbar-title {
  font-size: 16px;
  font-weight: 600;
  color: #1A1D23;
  margin: 0;
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.topbar-number {
  font-weight: 400;
  color: #2D4B95;
  font-size: 14px;
}
.topbar-rdi {
  font-size: 12px;
  color: #6B7280;
  margin: 3px 0 0;
  display: flex;
  align-items: center;
  gap: 4px;
}
.topbar-right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}
.inline-link-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0 2px;
  display: inline-flex;
  align-items: center;
  vertical-align: middle;
}

/* STATUS STRIP */
.status-strip {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 24px;
  background: #FAFBFF;
  border-bottom: 1px solid #E5E8EF;
  flex-wrap: wrap;
  flex-shrink: 0;
}
.status-strip-pills {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  flex: 1;
}
.status-strip-actions {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  margin-left: auto;
}

/* PILLS */
.pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}
.pill--blue   { background: #EEF2FF; color: #2D4B95; border: 1px solid #C7D2FE; }
.pill--green  { background: #F0FDF4; color: #15803D; border: 1px solid #BBF7D0; }
.pill--orange { background: #FFFBEB; color: #B45309; border: 1px solid #FDE68A; }
.pill--gray   { background: #F3F4F6; color: #4B5563; border: 1px solid #E5E7EB; }
.pill--private  { background: #FEF2F2; color: #B91C1C; border: 1px solid #FECACA; }
.pill--unseen   { background: #EFF6FF; color: #1D4ED8; border: 1px solid #BFDBFE; }
.pill--seen     { background: #F0FDF4; color: #15803D; border: 1px solid #BBF7D0; }
.pill--priority-high   { background: #FEF2F2; color: #B91C1C; border: 1px solid #FECACA; }
.pill--priority-medium { background: #FFFBEB; color: #B45309; border: 1px solid #FDE68A; }
.pill--priority-low,
.pill--priority-normal { background: #F3F4F6; color: #4B5563; border: 1px solid #E5E7EB; }

/* TWO-PANEL BODY */
.modal-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}
.modal-body.resizing {
  cursor: col-resize !important;
  user-select: none !important;
}

/* Detail panel (left) */
.panel-detail {
  min-width: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}
.panel-scroll {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}
.panel-scroll::-webkit-scrollbar { width: 4px; }
.panel-scroll::-webkit-scrollbar-track { background: transparent; }
.panel-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }

/* Resizer */
.panel-resizer {
  width: 12px;
  cursor: col-resize;
  position: relative;
  user-select: none;
  z-index: 100;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  flex-shrink: 0;
}
.panel-resizer::before {
  content: '';
  position: absolute;
  top: 0; bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 2px;
  background: #E5E8EF;
  transition: background 0.2s;
}
.panel-resizer:hover::before { background: #5E85EF; }
.resize-handle {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #E8EEFA;
  border: 1px solid #C8D4F0;
  position: relative;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.2s;
}
.panel-resizer:hover .resize-handle { opacity: 1; }

/* Resize overlay (prevents iframe stealing events) */
.resize-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 9999;
  cursor: col-resize;
  user-select: none;
  background: transparent;
}

/* Interactions panel (right) */
.panel-interactions {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #FAFBFF;
  border-left: 1px solid #E5E8EF;
  flex: 1 1 0;
  min-width: 0;
}
.panel-interactions--editor-open {
  overflow-y: auto;
}
.panel-interactions--editor-open .interactions-feed {
  flex: none;
  overflow-y: visible;
}
.panel-interactions--editor-open .compose-area {
  max-height: none;
  overflow-y: visible;
}
.interactions-header {
  padding: 14px 18px;
  border-bottom: 1px solid #E5E8EF;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
}
.interactions-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}
.interactions-feed {
  flex: 1;
  overflow-y: auto;
  padding: 12px 18px;
}
.interactions-feed::-webkit-scrollbar { width: 4px; }
.interactions-feed::-webkit-scrollbar-track { background: transparent; }
.interactions-feed::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 4px; }
.empty-interactions {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 24px;
}

/* META SECTION */
.meta-section { margin-bottom: 20px; }
.meta-two-columns {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 16px;
}
.meta-left-column,
.meta-right-column {
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.meta-item { display: flex; flex-direction: column; gap: 3px; }
.meta-item--association { padding-top: 4px; }
.meta-label { font-size: 11px; font-weight: 600; color: #9CA3AF; text-transform: uppercase; letter-spacing: 0.04em; }
.meta-value { font-size: 13px; color: #1A1D23; }
.meta-divider { margin: 12px 0 !important; }

/* CONTENT SECTIONS */
.content-section { margin-bottom: 20px; padding-top: 16px; border-top: 1px solid #F3F4F6; }
.section-title { font-size: 13px; font-weight: 600; color: #1A1D23; margin: 0 0 10px; }
.section-header-row { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
.text-action-btn {
  background: none;
  border: none;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #374151;
  padding: 4px 6px;
  border-radius: 6px;
  transition: background 0.15s;
}
.text-action-btn:hover { background: #F3F4F6; }

/* DESCRIPTION */
.description-body { font-size: 13px; color: #374151; line-height: 1.6; margin-bottom: 12px; }
.description-body::v-deep p { margin-bottom: 6px; }

/* ATTACHMENTS */
.attachments-list { display: flex; flex-direction: column; gap: 4px; }
.attachment-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 10px;
  border: 1px solid #E5E8EF;
  border-radius: 8px;
  background: white;
}
.attachment-info { display: flex; align-items: center; gap: 6px; min-width: 0; }
.attachment-name { font-size: 12px; color: #374151; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 200px; }
.attachment-actions { display: flex; align-items: center; gap: 2px; flex-shrink: 0; }
.empty-note { font-size: 12px; color: #9CA3AF; margin: 0; }

/* ICON BUTTONS */
.icon-action-btn {
  background: none;
  border: none;
  cursor: pointer;
  width: 26px;
  height: 26px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
}
.icon-action-btn:hover { background: #F3F4F6; }
.icon-action-btn.success:hover { background: #F0FDF4; }
.icon-action-btn.danger:hover  { background: #FEF2F2; }

/* SEARCH & CREATE */
.search-wrap {
  display: flex;
  align-items: center;
  gap: 4px;
  background: white;
  border: 1px solid #E5E8EF;
  border-radius: 8px;
  padding: 4px 8px;
}
.search-field {
  font-size: 12px;
  border: none;
  outline: none;
  background: transparent;
  width: 120px;
  color: #374151;
}
.create-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #2D4B95;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 5px 10px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.create-btn:hover { background: #1e3872; }
.create-menu {
  background: white;
  border: 1px solid #E5E8EF;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0,0,0,.1);
  padding: 4px;
  min-width: 180px;
}
.create-menu-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  border: none;
  background: none;
  padding: 8px 10px;
  border-radius: 7px;
  font-size: 13px;
  color: #1A1D23;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.create-menu-item:hover { background: #EEF2FF; }

/* COMPOSE AREA */
.compose-area {
  border-top: 1px solid #E5E8EF;
  padding: 12px 16px;
  background: white;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 55vh;
  overflow-y: auto;
}
.compose-type-row { margin-bottom: 4px; }
.reply-banner {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  background: #EEF2FF;
  border: 1px solid #C7D2FE;
  border-radius: 8px;
  padding: 8px 10px;
}
.reply-preview {
  font-size: 11px;
  color: #374151;
  max-height: 40px;
  overflow: hidden;
  text-overflow: ellipsis;
}
.reply-preview::v-deep p { margin: 0; }
.response-alert {
  display: flex;
  align-items: center;
  gap: 6px;
  background: #FFFBEB;
  border: 1px solid #FDE68A;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 11px;
  color: #B45309;
}
.compose-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.send-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: #2D4B95;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s;
}
.send-btn:hover:not(:disabled) { background: #1e3872; }
.send-btn:disabled { opacity: 0.6; cursor: not-allowed; }
.compose-secondary { display: flex; align-items: center; gap: 6px; }

/* TASK CLOSED NOTICE */
.task-closed-notice {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 14px;
  font-size: 12px;
  color: #15803D;
  background: #F0FDF4;
  border-top: 1px solid #BBF7D0;
  flex-shrink: 0;
}
.task-closed-notice-top {
  display: flex; align-items: center; gap: 8px;
  font-size: 13px; color: #15803D; flex-shrink: 0;
}

/* ATTACHMENTS PREVIEW */
.attachments-preview { display: flex; flex-direction: column; gap: 6px; }
.attach-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  color: #374151;
  padding: 4px 0;
}
.attach-dot {
  width: 8px; height: 8px;
  border-radius: 50%;
  background: #5E85EF;
}
.chevron { transition: transform 0.2s; }
.chevron.open { transform: rotate(180deg); }
.attach-grid { display: flex; flex-wrap: wrap; gap: 6px; }
.img-chip {
  position: relative;
  width: 60px; height: 60px;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid #E5E8EF;
}
.img-thumb { width: 100%; height: 100%; object-fit: cover; }
.chip-remove {
  position: absolute;
  top: 2px; right: 2px;
  background: rgba(0,0,0,.5);
  border: none;
  cursor: pointer;
  border-radius: 50%;
  width: 16px; height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.file-chip {
  display: flex;
  align-items: center;
  gap: 4px;
  background: white;
  border: 1px solid #E5E8EF;
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 12px;
}
.file-name { max-width: 120px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #374151; }
.chip-remove.file { position: static; background: none; width: auto; height: auto; color: #9CA3AF; }

/* LOADING STATE */
.loading-card {
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  overflow: hidden;
}
.loading-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  background: #1A2B5A;
}
.modal-brand { display: flex; align-items: center; gap: 10px; }
.brand-icon-dark {
  width: 32px; height: 32px;
  border-radius: 8px;
  background: rgba(255,255,255,.12);
  display: flex; align-items: center; justify-content: center;
}
.brand-label-dark { color: rgba(255,255,255,.9); font-size: 14px; font-weight: 600; }
.close-btn-dark {
  background: rgba(255,255,255,.1);
  border: none;
  cursor: pointer;
  width: 28px; height: 28px;
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
}
.close-btn-dark:hover { background: rgba(255,255,255,.2); }
.loading-body {
  padding: 48px 24px;
  background: #F8F9FC;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.loading-text { font-size: 13px; color: #6B7280; }

/* MOBILE */
@media (max-width: 768px) {
  /* La tarjeta es el contenedor de scroll → status-strip fluye y scrollea */
  .modal-card {
    border-radius: 0 !important;
    max-height: 100vh; height: 100vh;
    overflow-y: auto; overflow-x: hidden;
  }

  /* Topbar en columna; el sticky lo aporta ModalStickyHeader */
  .topbar {
    flex-direction: column; align-items: stretch;
    padding: 10px 16px; gap: 6px;
  }
  .topbar-right { order: -1; justify-content: flex-end; }
  .topbar-left { order: 0; }

  /* modal-body NO es scroll container en móvil */
  .modal-body {
    flex-direction: column;
    overflow: visible;
    min-height: unset;
  }

  .panel-detail {
    width: 100% !important; flex: none;
    border-right: none; border-bottom: 1px solid #E5E8EF;
    overflow: visible;
  }
  .panel-scroll { overflow: visible; padding: 14px 16px; flex: none; height: auto; }
  .panel-resizer { display: none; }

  .panel-interactions {
    width: 100% !important; flex: none; background: white;
    min-height: 400px; overflow: visible;
  }
  .interactions-feed { overflow: visible; min-height: 200px; padding: 12px 16px; flex: none; }

  .meta-two-columns { grid-template-columns: 1fr 1fr; gap: 12px; }

  .status-strip { padding: 8px 16px; flex-direction: column; align-items: flex-start; }
  .status-strip-actions { width: 100%; justify-content: flex-end; }
  .interactions-header { flex-wrap: wrap; gap: 8px; padding: 12px 16px; }
  .interactions-header-actions { width: 100%; justify-content: flex-end; }
  .search-field { width: 100%; }
  .search-wrap { flex: 1; }
  .compose-area { padding: 10px 14px; }
}

/* TEXT UTILITIES */
.text-primary { color: #2D4B95; }

/* STRIP INDICATORS */
.strip-separator { color: #D1D5DB; font-size: 14px; padding: 0 4px; line-height: 1; }
.strip-label { font-size: 11px; font-weight: 600; color: #6B7280; white-space: nowrap; }
.strip-no-indicators { font-size: 11px; color: #9CA3AF; }

/* ADD COMMENT BUTTON */
.add-comment-section { display: flex; align-items: center; padding: 8px 0; }
.add-comment-btn {
  display: inline-flex; align-items: center; gap: 8px;
  color: #2D4B95; cursor: pointer; border-radius: 8px;
  font-size: 13px; font-weight: 600; transition: all 0.15s;
  background: none; border: none; padding: 4px 8px;
}
.add-comment-btn:hover { background: #EEF2FF; }

.pill--autoclose {
  background: #EFF6FF;
  color: #1E40AF;
  border: 1px solid #93C5FD;
  font-size: 11px;
}
.pill--autoclose strong {
  font-weight: 700;
}
.pill--autoclose-urgent {
  background: #FFFBEB;
  color: #92400E;
  border: 1px solid #FCD34D;
  animation: pulse-border 1.5s ease-in-out infinite;
}
@keyframes pulse-border {
  0%, 100% { border-color: #FCD34D; box-shadow: none; }
  50%       { border-color: #F59E0B; box-shadow: 0 0 0 2px rgba(245,158,11,0.15); }
}

/* Estos sí van scoped normalmente */
.autoclose-tooltip-body {
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 240px;
}
.act-title {
  font-size: 12px;
  font-weight: 600;
  color: #1E40AF;
  gap: 6px;
}
.act-divider {
  height: 1px;
  background: #E0EAFF;
  margin: 2px 0;
}
.act-row {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11.5px;
  color: #374151;
}
.act-label {
  color: #6B7280;
  flex: 1;
}
.act-value {
  font-weight: 600;
  color: #1A1D23;
  text-align: right;
}
.act-value--green  { color: #15803D; }
.act-value--orange { color: #B45309; }
.act-value--blue   { color: #1E40AF; }
.act-note {
  margin-top: 4px;
  font-size: 10.5px;
  color: #9CA3AF;
  line-height: 1.5;
  border-top: 1px solid #F3F4F6;
  padding-top: 6px;
}
</style>
<style>
.autoclose-tooltip {
  background: #FFFFFF !important;
  border: 1.5px solid #93C5FD !important;
  border-radius: 10px !important;
  box-shadow: 0 4px 16px rgba(30, 64, 175, 0.10) !important;
  padding: 0 !important;
  opacity: 1 !important;
}
.autoclose-tooltip::after,
.autoclose-tooltip::before {
  display: none !important;
}
</style>